import { forwardRef } from 'react';

import { Button as BaseButton } from '@base-ui/react/button';
import { cn } from 'components/utils';

import CloseIcon from './close.svg?react';
import {
  tagContentRecipe,
  tagIconRecipe,
  tagLabelRecipe,
  tagRecipe,
  tagRemoveIconRecipe,
  tagRemoveRecipe,
} from './Tag.recipe';

import type { TagProps } from './types';

/**
 * Design System 2.0 tag.
 *
 * Ported from the Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 597:43779). Props mirror
 * the Figma variant axes, so "Primary Sm Deletable" reads straight across: `color`, `size`,
 * `onDelete`.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer in
 * `components/styles/design-system.css`, so the same markup is correct in both themes.
 *
 * A tag is DATA by default: no `onClick`, no `onDelete`, no tab stop, and no interactive element in
 * the tree at all. Both controls are opt-in, and both are opted into by passing the callback rather
 * than a boolean, so neither can exist without somewhere to report to.
 *
 * Distinct from `Badge` in `common/components`, which is a count indicator, and from a filter chip,
 * which toggles — the Figma set draws no selected cell, so this does not either.
 */
export const Tag = forwardRef<HTMLDivElement, TagProps>(
  ({ children, className, color, size, icon, disabled, onClick, onDelete, deleteLabel, ...rest }, ref) => {
    // The label and its icon, rendered identically whether or not they sit inside a button. A
    // variable rather than a nested component: a component defined in render would remount the
    // subtree on every parent render, and `react/no-unstable-nested-components` blocks it anyway.
    const content = (
      <>
        {icon && <span className={tagIconRecipe()}>{icon}</span>}
        <span className={tagLabelRecipe()}>{children}</span>
      </>
    );

    return (
      <div
        ref={ref}
        aria-disabled={disabled || undefined}
        className={cn(
          tagRecipe({
            color,
            size,
            interactive: Boolean(onClick || onDelete),
            deletable: Boolean(onDelete),
          }),
          className
        )}
        {...rest}
      >
        {/* The root is NEVER a button, even when the whole tag is clickable. A deletable, clickable
            tag would otherwise nest one button inside another, which is invalid HTML and leaves the
            inner control unreachable for assistive technology. Making the label the control and the
            × its sibling keeps them two independent controls at every combination. */}
        {onClick ? (
          <BaseButton className={tagContentRecipe({ interactive: true })} disabled={disabled} onClick={onClick}>
            {content}
          </BaseButton>
        ) : (
          <span className={tagContentRecipe()}>{content}</span>
        )}

        {onDelete && (
          <BaseButton
            // `string | undefined` here rather than `string`, because `deleteLabel` is destructured
            // out of the union and so cannot narrow off `onDelete`. The guarantee lives at the call
            // site, where `TagDeleteProps` makes `onDelete` without `deleteLabel` unconstructable —
            // which is the only place it can be enforced.
            aria-label={deleteLabel}
            className={tagRemoveRecipe({ size })}
            // A native `disabled` rather than `aria-disabled`: it takes the control out of the tab
            // order AND blocks activation by pointer and by keyboard, which is what a disabled tag
            // needs. Base UI mirrors it as `data-disabled` for the cursor rule.
            disabled={disabled}
            onClick={onDelete}
          >
            <CloseIcon className={tagRemoveIconRecipe()} />
          </BaseButton>
        )}
      </div>
    );
  }
);
